import classNames from "classnames";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";

import {
  Button,
  makeStyles,
  TableCell,
  TableCellProps,
  TableRow
} from "@material-ui/core";

import DisabledButtonWithTooltip from "src/components/DisabledButtonWithTooltip";
import {
  getCampaign_campaign,
  getCampaign_campaign_calleeFields
} from "src/graphql/generated/getCampaign";
import { CampaignStatus } from "src/graphql/generated/globalTypes";
import { getErrorMessage } from "src/util/errors";

import CsvColumnSelect from "./CsvColumnSelect";
import NameInput from "./NameInput";
import SaveFieldButton from "./SaveFieldButton";
import UnsavedChangeAlert from "./UnsavedChangeAlert";

const useStyles = makeStyles(theme => ({
  notMappedCopy: {
    color: theme.palette.text.secondary,
    fontStyle: "italic"
  },
  requiredAlert: {
    color: theme.palette.error.main,
    display: "block"
  },
  columnSelectorNotRequired: {
    marginBottom: "16px"
  }
}));

export default function CalleeFieldTableRow({
  calleeField,
  onEdit,
  onDelete,
  dataSourceFieldNames,
  onDirty,
  highlightUnsavedChanges,
  required,
  campaign,
  dataSourceFieldLabel
}: {
  calleeField: getCampaign_campaign_calleeFields;
  onEdit: (name: string, dataSourceFieldName: string | null) => any;
  onDelete: () => any;
  dataSourceFieldNames: string[];
  onDirty?: (dirty: boolean) => any;
  highlightUnsavedChanges?: boolean;
  required: false | { reason: string };
  campaign: getCampaign_campaign;
  dataSourceFieldLabel: string;
}): JSX.Element {
  const [isEditing, toggleIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(calleeField.name);
  const [editedDataMapping, setEditedDataMapping] = useState(
    calleeField.dataSourceFieldName
  );

  const snackbar = useSnackbar();

  const isDirty = useMemo(
    () =>
      editedName !== calleeField.name ||
      editedDataMapping !== calleeField.dataSourceFieldName,
    [calleeField, editedName, editedDataMapping]
  );

  useEffect(() => onDirty && onDirty(isDirty), [isDirty, onDirty]);

  const resetEdits = () => {
    setEditedName(calleeField.name);
    setEditedDataMapping(calleeField.dataSourceFieldName);
  };

  const handleSubmitChanges = async () => {
    try {
      await onEdit(editedName, editedDataMapping);
      toggleIsEditing(false);
    } catch (e) {
      snackbar.enqueueSnackbar(`Error updating field: ${getErrorMessage(e)}`, {
        variant: "error"
      });
    }
  };

  const cancelEdits = () => {
    toggleIsEditing(false);
    resetEdits();
  };

  const classes = useStyles();

  function renderDeleteButton() {
    if (calleeField.builtInField) {
      return (
        <DisabledButtonWithTooltip tooltipTitle="Built-in fields cannot be deleted">
          Delete
        </DisabledButtonWithTooltip>
      );
    } else if (required) {
      return (
        <DisabledButtonWithTooltip tooltipTitle="Cannot delete fields used in scripts">
          Delete
        </DisabledButtonWithTooltip>
      );
    } else {
      return <Button onClick={onDelete}>Delete</Button>;
    }
  }

  return isEditing ? (
    <>
      {highlightUnsavedChanges && isDirty ? <UnsavedChangeAlert /> : null}
      <TableRow>
        <ControlsCell>
          <SaveFieldButton
            onClick={handleSubmitChanges}
            disabled={!editedName.length || !isDirty}
          />
          <Button onClick={cancelEdits}>Cancel</Button>
        </ControlsCell>
        <NameCell>
          {calleeField.builtInField ? (
            calleeField.name
          ) : (
            <NameInput value={editedName} onChange={setEditedName} />
          )}
        </NameCell>
        <MappingCell>
          <CsvColumnSelect
            id={calleeField.id || calleeField.builtInField || "blah"}
            value={editedDataMapping}
            onChange={setEditedDataMapping}
            options={dataSourceFieldNames}
            className={classNames({
              [classes.columnSelectorNotRequired]: !required
            })}
            withNoneOption={
              !required || campaign.status === CampaignStatus.DRAFT
            }
            dataSourceFieldLabel={dataSourceFieldLabel}
          />
          {required && !editedDataMapping ? (
            <span className={classes.requiredAlert}>
              Required - {required.reason}
            </span>
          ) : null}
        </MappingCell>
      </TableRow>
    </>
  ) : (
    <TableRow>
      <ControlsCell>
        <Button onClick={() => toggleIsEditing(true)}>Edit</Button>
        {renderDeleteButton()}
      </ControlsCell>
      <NameCell>{calleeField.name}</NameCell>
      <MappingCell>
        {calleeField.dataSourceFieldName || (
          <>
            <span className={classes.notMappedCopy}>
              Not mapped to a column
            </span>
            {required ? (
              <span className={classes.requiredAlert}>
                Required - {required.reason}
              </span>
            ) : null}
          </>
        )}
      </MappingCell>
    </TableRow>
  );
}

const cellHeight = {
  height: "85px"
};

export const useControlsCellStyles = makeStyles(() => ({
  root: {
    ...cellHeight,
    width: "200px"
  }
}));

export const useNameCellStyles = makeStyles(() => ({
  root: {
    ...cellHeight,
    flex: "1"
  }
}));

export const useMappingCellStyles = makeStyles(() => ({
  root: {
    ...cellHeight,
    flex: "1"
  }
}));

export function ControlsCell(props: TableCellProps): JSX.Element {
  return (
    <TableCell key="controls" classes={useControlsCellStyles()} {...props} />
  );
}

export function NameCell(props: TableCellProps): JSX.Element {
  return <TableCell key="name" classes={useNameCellStyles()} {...props} />;
}

export function MappingCell(props: TableCellProps): JSX.Element {
  return (
    <TableCell key="mapping" classes={useMappingCellStyles()} {...props} />
  );
}
